/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/
import { ProtocolTypes } from 'utils/ProtocolTypes';
import { generateFileName } from 'utils/common';

export default class AbstractKafkaCodeSnippet {
  constructor(apiHandler, channelName, selectedCredentials, translate, isUserLoggedIn, operationId) {
    this.apiHandler = apiHandler;
    this.channelName = channelName;
    this.selectedCredentials = selectedCredentials;
    this.translate = translate;
    this.isUserLoggedIn = isUserLoggedIn;
    this.operationId = operationId;

    this.DEFAULT_AVRO_DESERIALIZER_TYPE = 'byte';
    this.DEFAULT_ASYNC_API_DESERIALIZER_TYPE = 'byte';
  }

  getBootstrapServers() {
    throw new Error('getBootstrapServers is not implemented');
  }

  getSchemaInformation() {
    const { type: schemaType } = this.apiHandler.getFirstSchema(this.channelName, this.operationId);

    const isAvro = this.apiHandler.isAvroSchema(schemaType);

    return {
      isAvro,
      fileName: generateFileName(this.channelName, isAvro ? 'avsc' : 'json')
    };
  }

  getKeyDeserializer() {
    return '';
  }

  getEncodingType() {
    const { encodingType } = this.apiHandler.getFirstSchema(this.channelName, this.operationId);
    return {
      JSONEncoded: encodingType === 'application/json'
    };
  }

  getApplicationDetails() {
    let applicationDetails = {};

    if (this.apiHandler.isEnforced()) {
      // Temporary fix for https://github.ibm.com/velox/events-planning/issues/628
      applicationDetails.groupId = 1;
      // old behaviour: applicationDetails.groupId = get(this.selectedCredentials, 'application', null);
    }

    return applicationDetails;
  }

  getEnforcedType() {
    return this.apiHandler.isEnforced();
  }

  getOperationBindings() {
    let operationBindings = {};
    const channel = this.apiHandler.api.channels().get(this.channelName);
    let bindings = channel.operations().get(this.operationId)?.bindings();
    if (bindings && bindings.all().length > 0 && bindings.get('kafka').value().clientId) {
      operationBindings.clientId = this.getSingleGroupClientId(bindings.get('kafka').value().clientId);
    }
    if (this.isUserLoggedIn && (!operationBindings.clientId || !this.getApplicationDetails().groupId)) {
      operationBindings.comment = this.translate('contact_api_owner');
    }
    return operationBindings;
  }

  getSingleGroupClientId(binding) {
    if (binding.enum && binding.enum.length > 1) {
      return null;
    } else if (binding.enum && binding.enum.length === 1) {
      return binding.enum[0];
    } else if (binding.const) {
      return binding.const;
    } else {
      return null;
    }
  }

  sasl_ssl() {
    throw new Error('sasl_ssl is not implemented');
  }

  sasl_plain() {
    throw new Error('sasl_plain is not implemented');
  }

  ssl() {
    throw new Error('ssl is not implemented');
  }

  ssl_mutualtls() {
    throw new Error('ssl_mutualtls is not implemented');
  }

  plaintext() {
    throw new Error('plaintext is not implemented');
  }

  sasl_sha_256() {
    throw new Error('sasl_sha_256 is not implemented');
  }

  sasl_sha_512() {
    throw new Error('sasl_sha_512 is not implemented');
  }

  sasl_ssl_sha_256() {
    throw new Error('sasl_ssl_sha_256 is not implemented');
  }

  sasl_ssl_sha_512() {
    throw new Error('sasl_ssl_sha_512 is not implemented');
  }

  getSecurityDetails() {
    const { type: securityType } = this.apiHandler.getFirstSecurityType();
    const protocol = this.apiHandler.getFirstProtocol();
    let result;

    if (protocol === ProtocolTypes.kafkaSecure) {
      switch (securityType) {
        case 'plain':
        case 'userPassword':
          result = this.sasl_ssl();
          break;
        case 'X509':
          result = this.ssl_mutualtls();
          break;
        case 'scramSha256':
          result = this.sasl_ssl_sha_256();
          break;
        case 'scramSha512':
          result = this.sasl_ssl_sha_512();
          break;
        case 'gssapi':
        default:
          result = this.ssl();
      }
    } else if (protocol === ProtocolTypes.kafka) {
      switch (securityType) {
        case 'plain':
        case 'userPassword':
          result = this.sasl_plain();
          break;
        case 'X509':
          result = this.ssl_mutualtls();
          break;
        case 'scramSha256':
          result = this.sasl_sha_256();
          break;
        case 'scramSha512':
          result = this.sasl_sha_512();
          break;
        case 'gssapi':
        default:
          result = this.plaintext();
      }
    }

    return {
      ...result,
      truststoreWarning: this.translate('truststore_warning'),
      saslUsername: this.selectedCredentials.credential
    };
  }
}
